'use client';
import { Alert } from '@vs-shop/ui/alert';
import { Button } from '@vs-shop/ui/button';
import { Heading } from '@vs-shop/ui/heading';
import { Input } from '@vs-shop/ui/input';
import { Textarea } from '@vs-shop/ui/textarea';
import { captureCause } from '../../../../../shared/utils/sentry';
import { Exit } from 'effect';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { contactUsAtomFn } from '../../../../../effect/atom/email/contactUsAtomFn';
import type { ContactUsFormData } from '@vs-shop/schemas/contactUsForm';
import { ContactUsFormSchema } from '@vs-shop/schemas/contactUsForm';
import { useAtomSet } from '@effect/atom-react';

export default function ContactUsProductCartForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const intl = useIntl();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ContactUsFormData>({
    resolver: effectTsResolver(ContactUsFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      message: '',
    },
  });

  const contactUs = useAtomSet(contactUsAtomFn, { mode: 'promiseExit' });

  const onSubmit = (data: ContactUsFormData) => {
    setErrorMessage(null);

    startTransition(async () => {
      const result = await contactUs({ email: data.email, message: data.message });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'ContactUsProductCartForm',
            action: 'Send contact message failed',
            additionalData: {
              email: data.email,
            },
          });

          setErrorMessage(
            intl.formatMessage({
              id: 'component.contactUsForm.apiError',
              defaultMessage: 'Došlo k chybě při odesílání zprávy, zkuste to prosím znovu.',
            })
          );
        },
        onSuccess: () => {
          setIsSubmitted(true);
          reset();
        },
      });
    });
  };

  if (isSubmitted) {
    return (
      <div className="rounded-lg bg-background p-2 ring ring-foreground/20">
        <Heading level={3} className="mb-2 text-primary">
          {intl.formatMessage({
            id: 'component.contactUsForm.success.heading',
            defaultMessage: 'Zpráva odeslána',
          })}
        </Heading>
        <p className="text-foreground">
          {intl.formatMessage({
            id: 'component.contactUsForm.success.message',
            defaultMessage: 'Děkujeme za vaši zprávu. Ozveme se vám co nejdříve.',
          })}
        </p>
        <Button
          size="sm"
          className="mt-4"
          onClick={() => {
            setIsSubmitted(false);
          }}
        >
          {intl.formatMessage({
            id: 'component.contactUsForm.success.button',
            defaultMessage: 'Odeslat další zprávu',
          })}
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-4">
      {errorMessage && (
        <Alert variant="destructive" role="alert">
          {errorMessage}
        </Alert>
      )}

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <Input
          {...register('email')}
          id="email"
          type="email"
          className="col-span-2"
          label={intl.formatMessage({
            id: 'component.contactUsForm.email.label',
            defaultMessage: 'E-mail',
          })}
          error={errors.email?.message ?? null}
          hasValue={!!watch('email')}
        />

        <div className="flex flex-col gap-2">
          <Textarea
            {...register('message')}
            id="contact-message"
            placeholder={intl.formatMessage({
              id: 'component.contactUsForm.message.placeholder',
              defaultMessage: 'Zpráva',
            })}
            resizable={false}
            error={errors.message?.message ?? null}
          />
        </div>

        <Button
          type="submit"
          variant="ghost"
          className="w-full uppercase"
          loading={isPending}
          disabled={isPending}
        >
          {intl.formatMessage({
            id: 'component.contactUsForm.button.send',
            defaultMessage: 'Odeslat zprávu',
          })}
        </Button>
      </form>
    </div>
  );
}
